import { Component, ElementRef, OnInit, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';

import { Utility } from '../../../../@core/utils/utility';
import {
  CustomExercise,
  CustomExerciseData,
} from '../../../../@core/interfaces/common/CalorieFriend/custom-exercise';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { UserData } from 'app/@core/interfaces/common/users';
import { ExerciseData } from 'app/@core/interfaces/common/CalorieFriend/exercise';

/**
 * Component for creation/edition of a Custom Exercise item
 */
@Component({
  selector: 'app-customexercise-def',
  templateUrl: './customexercise-def.component.html',
  styleUrls: ['./customexercise-def.component.scss'],
})
export class CustomExerciseDefComponent
  extends BaseComponentCanDeactivate
  implements OnInit {
  get IsDirty() {
    return this.isDirty;
  }

  HeaderLabel = '';
  NewExerciseItemLabel = 'Add Exercise Item';
  EditExerciseItemLabel = 'Edit Exercise Item';

  ExerciseNameLabel = $localize`:@@ExerciseName:Exercise Name`;
  DescriptionLabel = 'Description';
  DefaultServingSizeLabel = $localize`:@@DefaultServingSize:Default Serving Size`;

  GeneralInfoLabel = 'Nutrition Facts';
  GeneralInfoSmallLabel = 'About 100 grams';

  CaloriesLabel = 'Calories';
  FatsLabel = $localize`:@@Fats:Fats`;
  SaturatedFatLabel = 'Saturated Fat';
  TransFatLabel = 'Trans Fat';

  CholesterolLabel = 'Cholesterol';
  SodiumLabel = 'Sodium';

  CarbohydrateLabel = $localize`:@@Carbohydrate:Carbohydrate`;
  FiberLabel = 'Fiber';
  SugarLabel = 'Sugar';
  ProteinLabel = $localize`:@@Protein:Protein`;
  AlcoholLabel = 'Alcohol';

  SaveLabel = 'Save Item';
  BackLabel = 'Back';
  Created = 'Created';
  Updated = 'Updated';
  ProblemSavingExerciseItem = 'Problem occurred saving the Exercise Item';
  DisableDate = '0';

  ExerciseItemForm: FormGroup;

  ID = 0;
  IsPublic: boolean = false;
  currentUserID: any = null;
  isLoading = false;

  get IsForEdit() {
    return true;
  }

  protected readonly unsubscribe$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: NbDialogRef<CustomExerciseDefComponent>,
    private location: Location,
    private customExerciseService: CustomExerciseData,
    private usersService: UserData,
    private exerciseService: ExerciseData,
    private toasterService: NbToastrService,
    dialogService: NbDialogService,
    private cdr: ChangeDetectorRef
  ) {
    super(dialogService);
    const loadUser = this.usersService.getCurrentUser();
    loadUser.subscribe((user) => {
      this.currentUserID = user.id;
    });
  }

  get TitleLabel() {
    let label = '';
    if (!this.ID) {
      label = $localize`:@@AddExerciseItem:Add Exercise Item`;
    } else {
      if (!this.currentUserID) {
        label = 'Loading...';
      } else {
        label = $localize`:@@EditExerciseItem:Edit Exercise Item`;
      }
    }
    return label;
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.InitUserForm();

    if (this.ID === null || this.ID === 0) {
      this.HeaderLabel = this.NewExerciseItemLabel;
      this.DisableDate = '0';
    } else {
      this.HeaderLabel = this.EditExerciseItemLabel;
      this.loadExerciseItemData(this.ID);
      this.DisableDate = '0';
    }

    this.onChanges();
  }

  /**
   * Set up the event handlers of the input controls of the form
   * so it should calculate the total Calorie automatically reflecting the changes
   */
  onChanges(): void { }

  get Name() {
    return this.ExerciseItemForm.get('Name');
  }
  get ImageName() {
    return this.ExerciseItemForm.get('ImageName');
  }
  get VideoName() {
    return this.ExerciseItemForm.get('VideoName');
  }

  get ExerciseImageUrl() {
    return this.ExerciseItemForm.get('ExerciseImageUrl');
  }
  get ExerciseVideoUrl() {
    return this.ExerciseItemForm.get('ExerciseVideoUrl');
  }
  get YoutubeUrl() {
    return this.ExerciseItemForm.get('YoutubeUrl');
  }

  get exerciseVideoThumbnailUrl() {
    return this.ExerciseItemForm.get('exerciseVideoThumbnailUrl');
  }

  urlOrFileValidator(control: FormGroup): ValidationErrors | null {
    const url = control.get('YoutubeUrl');
    const file = control.get('ExerciseVideoUrl');
    const imageFile = control.get('ExerciseImageUrl');
    if(!(url?.value || file?.value) && !imageFile?.value) {
      return { 'addOne': true };
    }
    return url?.value && file?.value ? { 'urlOrFile': true } : null;
}

  /**
   * Initializes the form to create/edit a custom exercise item
   */
  InitUserForm() {
    this.ExerciseItemForm = this.fb.group({
      Name: this.fb.control(null, [Validators.required, Validators.minLength(1)]),
      ImageName: this.fb.control(null, []),
      VideoName: this.fb.control(null, []),
      FileExerciseImage: this.fb.control(null, []),
      ExerciseImageUrl: this.fb.control(null, []),
      YoutubeUrl: this.fb.control(null, [Validators.pattern(/^https?:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})$/gi)]),
      FileExerciseVideo: this.fb.control(null, []),
      ExerciseVideoUrl: this.fb.control(null, []),
      exerciseVideoThumbnailUrl: this.fb.control(null, []),
    }, { validators: this.urlOrFileValidator });
    super.initForm(this.ExerciseItemForm);
  }

  /**
   * It closes the dialog if it is opened on it.
   * If not, it goes back to the previous page.
   */
  Back() {
    super.back(() => {
      if (this.dialogRef) this.dialogRef.close();
      else this.location.back();
    });
  }

  /**
   * Converts the object given as a parameter to the 'Exercise' type
   * @param value Object to convert into 'Exercise' type
   */
  ConvertToExerciseItem(value: any): CustomExercise {
    if (!value.ExerciseImageUrl || value.ExerciseImageUrl === 'null')
      value.ExerciseImageUrl = null;
    if (!value.ExerciseVideoUrl || value.ExerciseVideoUrl === 'null')
      value.ExerciseVideoUrl = null;

    const customExercise: CustomExercise = value;

    return customExercise;
  }

  /**
   * It converts the inputs of the form into the Custom Exercise type
   * and submit the form to save them
   */
  SaveExerciseItem() {
    const ExerciseItem: CustomExercise = this.ConvertToExerciseItem(
      this.ExerciseItemForm.value,
    );

    ExerciseItem.id = this.ID ?? 0;

    const observable =
      ExerciseItem.id === null || ExerciseItem.id === 0
        ? this.customExerciseService.create(ExerciseItem)
        : this.customExerciseService.update(ExerciseItem);

    super.resetDirty();
    this.isLoading = true;
    observable.pipe(takeUntil(this.unsubscribe$),finalize(()=> {
      this.isLoading = false;
    })).subscribe(
      () => {
        this.handleSuccessResponse();
        this.exerciseService.ClearCache();
      },
      (err) => {
        this.handleWrongResponse();
      },
    );
  }

  /**
   * Handler of Success event of submit
   */
  handleSuccessResponse() {
    super.resetDirty();

    if (this.ID === null)
      this.toasterService.success('', `Item ${this.Created}!`);
    else this.toasterService.success('', `Item ${this.Updated}!`);

    this.Back();
  }

  /**
   * Handler of Fail event of submit
   */
  handleWrongResponse() {
    this.toasterService.danger('', this.ProblemSavingExerciseItem);
  }

  replaceNullStringWithNull(value: string): string {
    if (!value || value === 'null' || value === 'undefined') return null;
    return value;
  }

  /**
   * Loads the data of the Custom Exercise given by {id}
   * @param id The identifier of the Custom Exercise item
   */
  loadExerciseItemData(id) {
    if (id) {
      this.customExerciseService
        .get(id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((item) => {
          this.ExerciseItemForm.patchValue({
            ID: this.ID,
            Name: this.replaceNullStringWithNull(item.name),
            ImageName: this.replaceNullStringWithNull(item.imageName),
            VideoName: this.replaceNullStringWithNull(item.videoName),
            ExerciseImageUrl: this.replaceNullStringWithNull(item.exerciseImageUrl),
            ExerciseVideoUrl: this.replaceNullStringWithNull(item.exerciseVideoUrl),
            YoutubeUrl: this.replaceNullStringWithNull(item.youtubeUrl),
            exerciseVideoThumbnailUrl: this.replaceNullStringWithNull(item.exerciseVideoThumbnailUrl),
          });
          this.videoExerciseItem = this.replaceNullStringWithNull(item.exerciseVideoUrl);
          this.imgExerciseItem = this.replaceNullStringWithNull(item.exerciseImageUrl);
          if (!this.imgExerciseItem || this.imgExerciseItem === '') {
            this.imgExerciseItem = null;
          }
          this.ExerciseItemForm.updateValueAndValidity();
          super.resetDirty();
        });
    }
  }


  
  public getVideoUrl(source:string):string {
    return source + "#t=0.001";
  }

  onYoutubeUrlBlur(): void {
    let url: string = this.YoutubeUrl.value;

    //convert share link to youtube link
    //example: https://youtu.be/JTifbDPZEE8

    if (url.toLowerCase().indexOf("youtu.be/") > 0) {
      let newUrl = "https://www.youtube.com/watch?v=" + url.split("youtu.be/")[1];
      this.YoutubeUrl.setValue(newUrl);
      url = newUrl;
    }


    if (
      (this.imgExerciseItem == null || this.imgExerciseItem === '') &&
      url.indexOf('v=') > 0
    ) {
      const id: string = url.split('v=')[1];
      const finalUrl: string =
        'https://img.youtube.com/vi/' + id + '/hqdefault.jpg';
      this.ExerciseImageUrl.setValue(finalUrl);
      this.imgExerciseItem = finalUrl;
    }
  }
  /**
   * Validator that validates the number with the ##.# pattern
   */
  validateNumber = Utility.validateNumber;

  imgHover: boolean = false;
  imgExerciseItem: any = null;
  videoExerciseItem: any = null;

  /**
   * Event handler of uploading the exercise image
   * @param event Event object
   */
  onUploadExerciseImage(event): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      const mimeType = file.type;
      if (mimeType.match(/image\/*/) == null) {
        return;
      }

      this.ExerciseItemForm.patchValue({
        FileExerciseImage: file,
        ExerciseImageUrl: ' ',
      });

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (_event) => {
        this.imgExerciseItem = reader.result;
      };
    }
  }

  /**
   * Event handler of uploading the exercise image
   * @param event Event object
   */
  onUploadExerciseVideo(event): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      console.log(file.size)
      if(file.size > 75 * 1024 * 1024) {
        this.toasterService.danger('', 'Video size should be less than 75MB');
        return;
      }
      const mimeType = file.type;
      if (mimeType.match(/video\/*/) == null) {
        return;
      }

      this.ExerciseItemForm.patchValue({
        FileExerciseVideo: file,
        ExerciseVideoUrl: 'Uploaded',
      });

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (_event) => {
        this.videoExerciseItem = reader.result;
      };
    }
  }

  /**
   * Event handler for resetting the video
   */
  onResetExerciseVideo(fileInput: ElementRef = null) {
    this.videoExerciseItem = null;
    this.ExerciseItemForm.patchValue({
      ExerciseVideoUrl: null,
      FileExerciseVideo: null,
      exerciseVideoThumbnailUrl: null,
    });
    fileInput.nativeElement.value = null;
    this.cdr.detectChanges();
  }

  /**
   * Event handler for resetting the image
   */
  onResetExerciseImage(fileInput: ElementRef = null) {
    this.imgExerciseItem = null;
    this.ExerciseItemForm.patchValue({
      ExerciseImageUrl: null,
    });
    fileInput.nativeElement.value = null;
  }
}
